export interface Course {
  id: string;
  title: string;
  category: string;
  description: string;
  duration: string;
  students: string;
  level: string;
  rating: number;
  price: string;
  image: string;
}

export interface CourseDetails {
  priceValue: number; // NGN, used by Paystack (kobo = priceValue * 100)
  tagline: string;
  overview: string;
  audience: string[];
  modules: { title: string; topics: string[] }[];
  outcomes: string[];
  certification: string;
  format: string;
  startDate: string;
}

export const courseDetails: Record<string, CourseDetails> = {
  '1': {
    priceValue: 150000,
    tagline: 'Your first step into the world of international trade.',
    overview:
      'The Basic Certificate in Export Management is a hands-on foundation program designed for absolute beginners. Over 8 focused weeks, you will learn the language, documentation and workflow of Nigerian and international export operations — from identifying a viable product to shipping your first consignment.',
    audience: [
      'Aspiring exporters with no prior experience',
      'Small business owners exploring foreign markets',
      'Fresh graduates seeking a career in trade',
      'Freelancers and consultants adding export services',
    ],
    modules: [
      { title: 'Foundations of Export', topics: ['What export really is', 'Global trade landscape', 'Export vs. domestic business', 'The Nigerian export ecosystem'] },
      { title: 'Product & Market Selection', topics: ['Choosing exportable products', 'Country demand research', 'Pricing basics', 'Understanding your buyer'] },
      { title: 'Documentation Essentials', topics: ['NXP form', 'Certificate of Origin', 'Bill of Lading', 'Commercial Invoice & Packing List'] },
      { title: 'Getting Paid Safely', topics: ['Advance payment', 'Letters of Credit', 'Bank drafts', 'Avoiding common scams'] },
      { title: 'Your First Shipment', topics: ['Freight forwarders', 'Port procedures at Apapa & Tin Can', 'Customs clearance basics', 'Post-shipment follow up'] },
    ],
    outcomes: [
      'Confidently describe end-to-end export operations',
      'Prepare a complete set of basic export documents',
      'Identify and evaluate exportable products',
      'Choose safe payment methods for first-time deals',
      'Work productively with clearing agents and freight forwarders',
    ],
    certification: 'LSEM Basic Certificate in Export Management (accredited).',
    format: '100% remote — live online classes scheduled with an instructor. Full payment only (no instalment option at this level).',
    startDate: 'Open — rolling admissions, apply anytime.',
  },
  '2': {
    priceValue: 350000,
    tagline: 'Move from basics to strategy — win in real export markets.',
    overview:
      'The Advanced Certificate takes you beyond the basics into strategy, market entry and risk management. Over 3 months you will build a full export plan for a real product, working alongside practicing exporters and mentors.',
    audience: [
      'Graduates of the Basic Certificate',
      'Junior export executives at trading firms',
      'Business owners actively exporting or preparing to',
      'Consultants advising SMEs on trade',
    ],
    modules: [
      { title: 'Strategic Market Entry', topics: ['Choosing between direct/indirect export', 'Distributors, agents, e-commerce', 'Country risk scoring', 'Entry timing'] },
      { title: 'Advanced Documentation & Compliance', topics: ['HS codes & tariff classification', 'NAFDAC, SONCAP & phytosanitary rules', 'Free trade agreements (AfCFTA, ECOWAS)', 'Rules of origin'] },
      { title: 'Export Finance', topics: ['Working capital for exporters', 'NEXIM & CBN facilities', 'Hedging FX risk', 'Insurance & bonds'] },
      { title: 'Logistics & Supply Chain', topics: ['Incoterms 2020 in practice', 'Multimodal shipping', 'Warehousing & consolidation', 'Cold chain'] },
      { title: 'Negotiation & Contracts', topics: ['International sales contracts', 'Dispute resolution', 'Cross-cultural negotiation', 'Sample deal walkthroughs'] },
    ],
    outcomes: [
      'Design a market-entry plan for a chosen country',
      'Handle regulatory approvals across multiple product lines',
      'Structure trade finance for medium-scale shipments',
      'Negotiate and draft airtight export contracts',
      'Manage risk across FX, logistics and compliance',
    ],
    certification: 'LSEM Advanced Certificate in Export Management with capstone project assessment.',
    format: '100% remote — live online classes scheduled with an instructor. Full payment only (no instalment option at this level).',
    startDate: 'Open — rolling admissions, apply anytime.',
  },
  '3': {
    priceValue: 550000,
    tagline: 'Become the export professional companies compete to hire.',
    overview:
      'A career-defining 5-month professional program covering the entire export lifecycle at professional depth. Designed for mid-career professionals aiming to lead export departments or run substantial export operations.',
    audience: [
      'Export managers and senior officers',
      'Manufacturers scaling international sales',
      'Trade finance and logistics professionals',
      'Consultants and civil servants in trade agencies',
    ],
    modules: [
      { title: 'Global Trade Environment', topics: ['WTO framework', 'Regional trade blocs', 'Trade policy analysis', 'Sanctions & compliance'] },
      { title: 'Export Operations Management', topics: ['Setting up an export department', 'SOPs & KPIs', 'Team structure', 'Tech stack for exporters'] },
      { title: 'Advanced Trade Finance', topics: ['Structured finance', 'Factoring & forfaiting', 'Export credit insurance', 'ESG-linked financing'] },
      { title: 'International Marketing', topics: ['Trade fairs & B2B lead-gen', 'Digital export marketing', 'Building distributor networks', 'Branding across cultures'] },
      { title: 'Risk & Crisis Management', topics: ['Political risk', 'Buyer default', 'Shipping disruptions', 'Reputation management'] },
      { title: 'Capstone', topics: ['End-to-end export business plan', 'Boardroom pitch', 'Peer review', 'Industry mentor sign-off'] },
    ],
    outcomes: [
      'Lead a full export department end-to-end',
      'Design SOPs and KPIs for export operations',
      'Structure complex trade finance deals',
      'Build and manage international sales channels',
      'Present investment-grade export business plans',
    ],
    certification: 'LSEM Professional Certificate in Export Management, recognised by industry partners.',
    format: '100% remote — weekly live online sessions with an instructor. Instalment payment plan available.',
    startDate: 'Open — rolling admissions, apply anytime.',
  },
  '4': {
    priceValue: 650000,
    tagline: 'A rigorous 6-month diploma with real deals, real mentors.',
    overview:
      'The Diploma in Export Management is a rigorous, academically structured qualification delivered 100% remotely through live instructor-led classes. Students sit continuous assessments, class activities, mid-term tests and a final examination, alongside assessed assignments on genuine export projects.',
    audience: [
      'Career switchers moving into export/trade',
      'Owners of growing export SMEs',
      'Junior-to-mid trade professionals',
      'Agribusiness, solid minerals and manufacturing operators',
    ],
    modules: [
      { title: 'Diploma Foundations', topics: ['Trade theory in practice', 'Product economics', 'Value chain mapping', 'Ethics in trade'] },
      { title: 'Documentation Mastery', topics: ['Full documentary cycle', 'Digital trade documents', 'Common documentary errors', 'Bank compliance'] },
      { title: 'Export Finance & Payments', topics: ['LC in depth', 'Documentary collections', 'Working with banks', 'CBN export proceeds rules'] },
      { title: 'Logistics in Practice', topics: ['Container operations', 'Reefer shipments', 'Groupage', 'Insurance claims walkthrough'] },
      { title: 'Academic Assessments', topics: ['Class activities & participation', 'Continuous assessment tests', 'Mid-term test', 'Final examination'] },
      { title: 'Assessed Project', topics: ['Choose product & market', 'Full export plan', 'Financial model', 'Live defence'] },
    ],
    outcomes: [
      'Manage the full documentary cycle without supervision',
      'Pass structured tests, class activities and a final examination',
      'Structure payments and financing for real deals',
      'Deliver a defended diploma project ready for execution',
    ],
    certification: 'LSEM Diploma in Export Management with graded transcript.',
    format: '100% remote — live instructor-led classes with tests, class activities and formal examinations. Instalment payment plan available.',
    startDate: 'Open — rolling admissions, apply anytime.',
  },
  '5': {
    priceValue: 1200000,
    tagline: 'Master-level training for exporters leading at scale.',
    overview:
      'An 8-month Advanced Diploma for senior professionals managing large-scale export operations, delivered 100% remotely through live instructor-led classes. The curriculum takes a strong academic approach — with graded class activities, continuous assessment tests and formal examinations — alongside strategy, leadership, international negotiation and enterprise-level trade finance.',
    audience: [
      'Heads of export & international trade',
      'CEOs and founders of export enterprises',
      'Senior public-sector officials in trade & customs',
      'Investment officers financing exporters',
    ],
    modules: [
      { title: 'Strategic Trade Leadership', topics: ['Setting export strategy', 'Board-level reporting', 'Governance in trade', 'Sustainability & ESG'] },
      { title: 'Global Negotiations', topics: ['High-value contract negotiation', 'Dispute resolution & arbitration', 'Cross-border joint ventures', 'Cultural intelligence'] },
      { title: 'Enterprise Trade Finance', topics: ['Syndicated trade facilities', 'Commodity finance', 'ECAs & DFIs', 'FX portfolio management'] },
      { title: 'Global Supply Chain Strategy', topics: ['Nearshoring & friendshoring', 'Digital supply chains', 'Trade data & analytics', 'Sanctions compliance at scale'] },
      { title: 'Academic Assessments', topics: ['Class activities & participation', 'Continuous assessment tests', 'Mid-term examination', 'Final examination'] },
      { title: 'Advanced Capstone', topics: ['Enterprise export strategy', 'Investor-grade pitch', 'External examiner defence'] },
    ],
    outcomes: [
      'Set and defend enterprise export strategy at board level',
      'Lead high-value international negotiations',
      'Structure enterprise-scale trade finance',
      'Excel across tests, class activities and final examinations',
      'Represent Nigerian exporters credibly on the world stage',
    ],
    certification: 'LSEM Advanced Diploma in Export Management, jointly assessed with industry examiners.',
    format: '100% remote — live instructor-led classes with structured tests, class activities and formal examinations. Instalment payment plan available.',
    startDate: 'Open — rolling admissions, apply anytime.',
  },
  '6': {
    priceValue: 2000000,
    tagline: 'An executive MBA-style program for global trade leaders.',
    overview:
      'The Mini MBA in International Trade is a 12-month executive program that fuses core MBA disciplines — strategy, finance, marketing, leadership — with deep specialisation in cross-border trade. Cohorts learn alongside senior peers from banks, ministries and multinational exporters.',
    audience: [
      'C-suite executives in trade-dependent businesses',
      'Senior bankers & trade finance leaders',
      'Government & regulatory leaders',
      'Founders scaling internationally',
    ],
    modules: [
      { title: 'Strategy & Leadership', topics: ['Global strategy', 'Executive decision-making', 'Leading high-performing teams', 'Change management'] },
      { title: 'Finance for Trade Leaders', topics: ['Corporate finance', 'Trade & structured finance', 'Valuation of export ventures', 'M&A in trade'] },
      { title: 'Global Marketing', topics: ['Country-market analytics', 'International branding', 'Channel design', 'Digital-first exports'] },
      { title: 'Trade Policy & Diplomacy', topics: ['Trade negotiations', 'AfCFTA implementation', 'Public–private trade partnerships', 'Advocacy'] },
      { title: 'Executive Residencies', topics: ['Lagos', 'Accra or Nairobi', 'Dubai or Guangzhou', 'Peer-led case labs'] },
      { title: 'Board Project', topics: ['Live enterprise challenge', 'Executive sponsor', 'Board defence'] },
    ],
    outcomes: [
      'Lead international trade strategy at executive level',
      'Evaluate and finance cross-border ventures',
      'Represent your organisation in trade negotiations',
      'Build a peer network across African & global trade',
      'Deliver a board-ready international growth plan',
    ],
    certification: 'LSEM Mini MBA in International Trade, with executive alumni network access.',
    format: '100% remote — live instructor-led executive sessions. Instalment payment plan available.',
    startDate: 'Open — rolling admissions, apply anytime.',
  },
  '7': {
    priceValue: 2000000,
    tagline: 'The pinnacle Nigerian qualification for export enterprise leaders.',
    overview:
      'A 12-month executive Mini MBA dedicated specifically to Export Management. Built for leaders who intend to build, scale or govern export enterprises — with heavy emphasis on Nigerian and African export economics, policy and enterprise operations.',
    audience: [
      'Owners of established export enterprises',
      'Executives at large exporters and trading houses',
      'Investors and DFIs backing exporters',
      'Senior policy makers in trade & industry',
    ],
    modules: [
      { title: 'Enterprise Export Strategy', topics: ['Building an export enterprise', 'Product diversification', 'Vertical integration', 'Export-led growth models'] },
      { title: 'Advanced Trade Finance & Structuring', topics: ['Structured commodity finance', 'Export receivables', 'Multi-currency structures', 'DFIs & guarantees'] },
      { title: 'Operations at Scale', topics: ['Multi-port operations', 'ERP for exporters', 'Quality systems (ISO, HACCP)', 'Regulatory relations'] },
      { title: 'Global Markets Deep-Dive', topics: ['EU market access', 'North America', 'Middle East & Asia', 'Intra-African trade under AfCFTA'] },
      { title: 'Leadership & Governance', topics: ['Corporate governance', 'Family enterprise dynamics', 'Sustainability & reporting', 'Succession planning'] },
      { title: 'Executive Residencies + Board Project', topics: ['International residency', 'Live enterprise project', 'External board defence'] },
    ],
    outcomes: [
      'Design and defend enterprise-level export strategy',
      'Structure sophisticated, multi-currency trade finance',
      'Run compliant, scalable multi-port operations',
      'Access and win in EU, US, MENA and Asian markets',
      'Govern and grow an export enterprise sustainably',
    ],
    certification: 'LSEM Mini MBA in Export Management — executive qualification.',
    format: '100% remote — live instructor-led executive sessions. Instalment payment plan available.',
    startDate: 'Open — rolling admissions, apply anytime.',
  },
};

export const courses = {
  certificate: [
    {
      id: '1',
      title: 'Basic Certificate in Export Management',
      category: 'Certificate',
      description: 'Build a solid foundation in export operations, documentation, and compliance. Ideal for beginners entering international trade.',
      duration: '8 weeks',
      students: '1,200+',
      level: 'Beginner',
      rating: 4.8,
      price: '₦150,000',
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80'
    },
    {
      id: '2',
      title: 'Advanced Certificate in Export Management',
      category: 'Certificate',
      description: 'Deepen your expertise with advanced export strategies, market entry techniques, and risk management.',
      duration: '3 months',
      students: '850+',
      level: 'Intermediate',
      rating: 4.9,
      price: '₦350,000',
      image: 'https://images.unsplash.com/photo-1553877522-43269d4ea984?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80'
    },
    {
      id: '3',
      title: 'Professional Certificate in Export Management',
      category: 'Certificate',
      description: 'Comprehensive professional training covering end-to-end export management for career advancement.',
      duration: '5 months',
      students: '600+',
      level: 'Advanced',
      rating: 4.9,
      price: '₦550,000',
      image: 'https://images.unsplash.com/photo-1507537297725-24a1c029d3ca?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80'
    }
  ],
  diploma: [
    {
      id: '4',
      title: 'Diploma in Export Management',
      category: 'Diploma',
      description: 'A thorough diploma program equipping you with practical skills in global trade, logistics, and export finance.',
      duration: '6 months',
      students: '700+',
      level: 'Intermediate',
      rating: 4.8,
      price: '₦650,000',
      image: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80'
    },
    {
      id: '5',
      title: 'Advanced Diploma in Export Management',
      category: 'Advanced Diploma',
      description: 'Master-level training in export leadership, international negotiations, and large-scale trade operations.',
      duration: '8 months',
      students: '450+',
      level: 'Advanced',
      rating: 4.9,
      price: '₦1,200,000',
      image: 'https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80'
    }
  ],
  'mini-mba': [
    {
      id: '6',
      title: 'Mini MBA in International Trade',
      category: 'Mini MBA',
      description: 'Executive-level program blending business administration with deep international trade expertise for global leaders.',
      duration: '12 months',
      students: '300+',
      level: 'Executive',
      rating: 5.0,
      price: '₦2,000,000',
      image: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80'
    },
    {
      id: '7',
      title: 'Mini MBA in Export Management',
      category: 'Mini MBA',
      description: 'The pinnacle export management qualification — strategic leadership, policy, and large-scale export enterprise management.',
      duration: '12 months',
      students: '250+',
      level: 'Executive',
      rating: 5.0,
      price: '₦2,000,000',
      image: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80'
    }
  ]
};

export const allCourses: Course[] = [
  ...courses.certificate,
  ...courses.diploma,
  ...courses['mini-mba']
];
